import { createBaseQuery, getBaseUrl } from "@/redux/config";
import { createApi } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

export const settlementReportApi = createApi({
  reducerPath: "settlementReportApi",
  baseQuery: createBaseQuery("/c/"),
  tagTypes: ["SettlementReport"],
  endpoints: (build) => ({
    generatePDFReport: build.mutation<
      Blob,
      {
        companyId: number;
        serviceId: number;
        startDate: string;
        endDate: string;
        categoryId?: number;
        title?: string;
        companyName?: string;
      }
    >({
      queryFn: async (args, api, _extraOptions, baseQuery) => {
        const {
          companyId,
          serviceId,
          startDate,
          endDate,
          categoryId,
          title,
          companyName,
        } = args;

        const params = new URLSearchParams({
          startDate,
          endDate,
          ...(categoryId && { categoryId: categoryId.toString() }),
          ...(title && { title }),
          ...(companyName && { companyName }),
        });

        try {
          const response = await fetch(
            `${getBaseUrl()}/api/v1/c/${companyId}/services/${serviceId}/product-txs/settlement-reports/pdf?${params}`
          );

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: "Failed to generate PDF",
              } as FetchBaseQueryError,
            };
          }

          const blob = await response.blob();
          return { data: blob };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "Unknown error",
            } as FetchBaseQueryError,
          };
        }
      },
    }),

    generateCSVReport: build.mutation<
      Blob,
      {
        companyId: number;
        serviceId: number;
        startDate: string;
        endDate: string;
        categoryId?: number;
      }
    >({
      queryFn: async (args, api, _extraOptions, baseQuery) => {
        const { companyId, serviceId, startDate, endDate, categoryId } = args;

        const params = new URLSearchParams({
          startDate,
          endDate,
          ...(categoryId && { categoryId: categoryId.toString() }),
        });

        try {
          const response = await fetch(
            `${getBaseUrl()}/api/v1/c/${companyId}/services/${serviceId}/product-txs/settlement-reports/csv?${params}`
          );

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: "Failed to generate CSV",
              } as FetchBaseQueryError,
            };
          }

          const blob = await response.blob();
          return { data: blob };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "Unknown error",
            } as FetchBaseQueryError,
          };
        }
      },
    }),

    getSettlementReportData: build.query<
      SettlementReportLine[],
      {
        companyId: number;
        serviceId: number;
        startDate: string;
        endDate: string;
        categoryId?: number;
      }
    >({
      query: ({ companyId, serviceId, startDate, endDate, categoryId }) => ({
        url: `${companyId}/services/${serviceId}/product-txs/settlement-reports/data`,
        method: "GET",
        params: {
          startDate,
          endDate,
          ...(categoryId && { categoryId }),
        },
      }),
      providesTags: ["SettlementReport"],
    }),

    generateDetailsPDFReport: build.mutation<
      Blob,
      {
        companyId: number;
        serviceId: number;
        startDate: string;
        endDate: string;
        categoryId?: number;
        title?: string;
        companyName?: string;
      }
    >({
      queryFn: async (args, _api, _extraOptions, _baseQuery) => {
        const {
          companyId,
          serviceId,
          startDate,
          endDate,
          categoryId,
          title,
          companyName,
        } = args;

        const params = new URLSearchParams({
          startDate,
          endDate,
          ...(categoryId && { categoryId: categoryId.toString() }),
          ...(title && { title }),
          ...(companyName && { companyName }),
        });

        try {
          const response = await fetch(
            `${getBaseUrl()}/api/v1/c/${companyId}/services/${serviceId}/product-txs/details-reports/pdf?${params}`
          );

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: "Failed to generate details PDF",
              } as FetchBaseQueryError,
            };
          }

          const blob = await response.blob();
          return { data: blob };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "Unknown error",
            } as FetchBaseQueryError,
          };
        }
      },
    }),

    generateDetailsCSVReport: build.mutation<
      Blob,
      {
        companyId: number;
        serviceId: number;
        startDate: string;
        endDate: string;
        categoryId?: number;
        title?: string;
        companyName?: string;
      }
    >({
      queryFn: async (args, _api, _extraOptions, _baseQuery) => {
        const {
          companyId,
          serviceId,
          startDate,
          endDate,
          categoryId,
          title,
          companyName,
        } = args;

        const params = new URLSearchParams({
          startDate,
          endDate,
          ...(categoryId && { categoryId: categoryId.toString() }),
          ...(title && { title }),
          ...(companyName && { companyName }),
        });

        try {
          const response = await fetch(
            `${getBaseUrl()}/api/v1/c/${companyId}/services/${serviceId}/product-txs/details-reports/csv?${params}`
          );

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: "Failed to generate details CSV",
              } as FetchBaseQueryError,
            };
          }

          const blob = await response.blob();
          return { data: blob };
        } catch (error) {
          return {
            error: {
              status: "CUSTOM_ERROR",
              error: error instanceof Error ? error.message : "Unknown error",
            } as FetchBaseQueryError,
          };
        }
      },
    }),
  }),
});
